import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import {
    createIntegrationLinks, deleteIntegrationLink, fetchIntegrationLinks, listGitRepositories,
    syncIntegrationLink, type IntegrationLinkDto, type IntegrationTargetDto,
    type IntegrationTargetsDto, type ProviderId, type VendorConnectionDto
} from "@/api/integrations/links";
import { listJiraProjects } from "@/api/integrations/atlassian";
import { listAzureDevOpsProjects } from "@/api/integrations/azureDevOps";
import { PROVIDERS, type ProviderMeta } from "./providerMeta";

export interface SourceRow {
    externalRef: string;
    externalName: string;
    keyOrBranch: string;
    metadata: Record<string, unknown>;
    links: IntegrationLinkDto[];
    /** True only when this target was returned by the provider's latest listing. */
    availableForLinking?: boolean;
}

export interface ProviderState {
    meta: ProviderMeta;
    connected: boolean;
    siteOrOrg: string;
    cloudId: string | null;
    organization: string | null;
    rows: SourceRow[];
    listingError: string | null;
    listingErrorRequiresReauth: boolean;
}

function isReauthRequiredError(error: unknown): boolean {
    if (!error || typeof error !== "object") {
        return false;
    }
    const details = (error as { details?: unknown }).details;
    if (!details || typeof details !== "object") {
        return false;
    }
    return (details as { requiresReauth?: unknown }).requiresReauth === true;
}

interface ListingItem {
    externalRef: string;
    externalName: string;
    keyOrBranch: string;
    metadata: Record<string, unknown>;
}

async function fetchListing(meta: ProviderMeta, dto: VendorConnectionDto, token: string): Promise<ListingItem[]> {
    if (meta.id === "jira") {
        if (!dto.cloudId) return [];
        const { projects } = await listJiraProjects(dto.cloudId, token);
        return (projects || []).map((p: { id: string; key: string; name: string }) => ({
            externalRef: p.key, externalName: p.name, keyOrBranch: p.key,
            metadata: { id: p.id, key: p.key }
        }));
    }
    if (meta.kind === "git") {
        const provider = meta.id === "azure-repos" ? "azure_repos" : meta.id;
        const { repositories } = await listGitRepositories(
            provider as "github" | "gitlab" | "bitbucket" | "azure_repos",
            token
        );
        return (repositories || []).map(r => {
            const metadata: Record<string, unknown> = {
                full_name: r.full_name,
                branch: r.default_branch
            };
            if (meta.id === "azure-repos") {
                const azureRepo = r as { id?: string; organization?: string; project?: string };
                if (azureRepo.id) {
                    metadata.id = azureRepo.id;
                    metadata.repository_id = azureRepo.id;
                }
                if (azureRepo.organization) {
                    metadata.organization = azureRepo.organization;
                }
                if (azureRepo.project) {
                    metadata.project = azureRepo.project;
                }
            }
            return {
                externalRef: r.clone_url || r.url || "",
                externalName: r.full_name || r.name,
                keyOrBranch: r.default_branch || "",
                metadata
            };
        }).filter(r => r.externalRef);
    }
    if (meta.id === "azure-devops") {
        if (!dto.organization) return [];
        const { projects } = await listAzureDevOpsProjects(dto.organization, token);
        return (projects || []).map(p => ({
            externalRef: p.id, externalName: p.name, keyOrBranch: p.id,
            metadata: { id: p.id, name: p.name }
        }));
    }
    return [];
}

function buildRows(listing: ListingItem[], links: IntegrationLinkDto[]): SourceRow[] {
    const rows = new Map<string, SourceRow>();
    for (const item of listing) {
        rows.set(item.externalRef, { ...item, links: [], availableForLinking: true });
    }
    // Index listing rows by metadata.id too, so links whose externalRef predates
    // the key/id convention (e.g. legacy Jira links backfilled with the numeric
    // project id instead of the key) can still be matched to their listing row.
    // Both sides may hold ids as strings or numbers, so compare as strings.
    const rowsByMetaId = new Map<string, SourceRow>();
    for (const row of rows.values()) {
        const metaId = row.metadata?.id;
        if (metaId !== undefined && metaId !== null) {
            rowsByMetaId.set(String(metaId), row);
        }
    }
    for (const link of links) {
        const existing = rows.get(link.externalRef);
        if (existing) {
            existing.links.push(link);
            continue;
        }
        const linkMetaId = link.metadata?.id;
        const byId = linkMetaId !== undefined && linkMetaId !== null
            ? rowsByMetaId.get(String(linkMetaId))
            : undefined;
        if (byId) {
            byId.links.push(link);
            continue;
        }
        rows.set(link.externalRef, {
            externalRef: link.externalRef,
            externalName: link.externalName,
            keyOrBranch: String(link.metadata?.key || link.metadata?.branch || ""),
            metadata: link.metadata,
            links: [link],
            availableForLinking: false
        });
    }
    return Array.from(rows.values());
}

export function useIntegrationsData(refreshSignal = 0) {
    const { getToken } = useAuth();
    // useAuth() from Clerk returns a fresh `getToken` closure on every render.
    // Keeping it out of the callback dependency arrays (via a ref) is what keeps
    // `refresh` referentially stable, so the mount effect below runs exactly once
    // instead of looping (fetch -> setState -> re-render -> new getToken -> re-fetch).
    const getTokenRef = useRef(getToken);
    getTokenRef.current = getToken;

    const [providers, setProviders] = useState<ProviderState[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [targets, setTargets] = useState<IntegrationTargetsDto>({ products: [], contextMeshes: [] });
    const refreshGenerationRef = useRef(0);

    const refresh = useCallback(async () => {
        const generation = ++refreshGenerationRef.current;
        setLoading(true);
        setError(null);
        try {
            const token = await getTokenRef.current();
            if (!token) throw new Error("No auth token available");
            const { vendors, targets: fetchedTargets } = await fetchIntegrationLinks(token);
            if (generation === refreshGenerationRef.current) {
                setTargets(fetchedTargets ?? { products: [], contextMeshes: [] });
            }
            const byProvider = new Map(vendors.map(v => [v.provider, v]));
            const next = await Promise.all(PROVIDERS.map(async meta => {
                const dto = byProvider.get(meta.id);
                const state: ProviderState = {
                    meta,
                    connected: Boolean(dto?.connected),
                    siteOrOrg: dto?.siteOrOrg || "",
                    cloudId: dto?.cloudId ?? null,
                    organization: dto?.organization ?? null,
                    rows: [],
                    listingError: null,
                    listingErrorRequiresReauth: false
                };
                if (!dto?.connected) return state;
                let listing: ListingItem[] = [];
                try {
                    listing = await fetchListing(meta, dto, token);
                } catch (e) {
                    state.listingError = e instanceof Error ? e.message : "Failed to load";
                    state.listingErrorRequiresReauth = isReauthRequiredError(e);
                }
                state.rows = buildRows(listing, dto.links);
                return state;
            }));
            if (generation === refreshGenerationRef.current) {
                setProviders(next);
            }
        } catch (e) {
            if (generation === refreshGenerationRef.current) {
                setError(e instanceof Error ? e.message : "Failed to load integrations");
            }
        } finally {
            if (generation === refreshGenerationRef.current) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        void refresh();
    }, [refresh, refreshSignal]);

    useEffect(() => {
        const refreshOnFocus = () => {
            void refresh();
        };
        const refreshOnVisible = () => {
            if (document.visibilityState === "visible") {
                void refresh();
            }
        };
        window.addEventListener("focus", refreshOnFocus);
        document.addEventListener("visibilitychange", refreshOnVisible);
        return () => {
            window.removeEventListener("focus", refreshOnFocus);
            document.removeEventListener("visibilitychange", refreshOnVisible);
        };
    }, [refresh]);

    const mutateRows = useCallback((fn: (p: ProviderState) => ProviderState) => {
        setProviders(prev => prev.map(fn));
    }, []);

    const linkTarget = useCallback(async (
        provider: ProviderId,
        row: SourceRow,
        target: Pick<IntegrationTargetDto, "type" | "id">
    ) => {
        const token = await getTokenRef.current();
        if (!token) throw new Error("No auth token available");
        const response = await createIntegrationLinks({
            provider,
            externalRef: row.externalRef,
            externalName: row.externalName,
            metadata: row.metadata,
            target
        }, token);

        // A listing refresh started before this mutation must not overwrite the
        // newly-created binding after the server confirms it.
        refreshGenerationRef.current += 1;
        setLoading(false);
        mutateRows(current => current.meta.id !== provider ? current : {
            ...current,
            rows: current.rows.map(source => source.externalRef !== row.externalRef ? source : {
                ...source,
                links: [
                    ...source.links.filter(existing =>
                        !response.links.some(created => created.id === existing.id)),
                    ...response.links
                ]
            })
        });
        return response;
    }, [mutateRows]);

    const linkProduct = useCallback(async (provider: ProviderId, row: SourceRow, projectId: number) => {
        const token = await getTokenRef.current();
        if (!token) throw new Error("No auth token available");
        const response = await createIntegrationLinks({
            provider, externalRef: row.externalRef, externalName: row.externalName,
            metadata: row.metadata, projectIds: [projectId]
        }, token);
        refreshGenerationRef.current += 1;
        setLoading(false);
        mutateRows(current => current.meta.id !== provider ? current : {
            ...current,
            rows: current.rows.map(source => source.externalRef !== row.externalRef ? source : {
                ...source,
                links: [...source.links.filter(existing =>
                    !response.links.some(created => created.id === existing.id)), ...response.links]
            })
        });
        return response;
    }, [mutateRows]);

    const unlink = useCallback(async (link: IntegrationLinkDto | number) => {
        const token = await getTokenRef.current();
        if (!token) throw new Error("No auth token available");
        const response = await deleteIntegrationLink(link, token);
        if (response.context_mesh_refresh?.scheduled === false) {
            console.warn(response.warning || "Context Mesh refresh was not scheduled.", {
                requestId: response.context_mesh_refresh.request_id
            });
        }
        // A refresh started before this mutation may contain the deleted link.
        // Invalidate it before applying the authoritative mutation result.
        refreshGenerationRef.current += 1;
        setLoading(false);
        mutateRows(p => ({
            ...p,
            rows: p.rows.map(row => ({
                ...row,
                links: row.links.filter(existing => existing.id !== (typeof link === "number" ? link : link.id))
            }))
        }));
    }, [mutateRows]);

    const syncLink = useCallback(async (link: IntegrationLinkDto | number) => {
        try {
            const token = await getTokenRef.current();
            if (!token) throw new Error("No auth token available");
            const response = await syncIntegrationLink(link, token);
            // Prevent an older GET/listing response from replacing this newer DTO.
            refreshGenerationRef.current += 1;
            setLoading(false);
            mutateRows(p => ({
                ...p,
                rows: p.rows.map(row => ({
                    ...row,
                    links: row.links.map(existing => existing.id === (typeof link === "number" ? link : link.id) ? response.link : existing)
                }))
            }));
            if (response.context_mesh_refresh?.scheduled === false) {
                console.warn(response.warning || "Context Mesh refresh was not scheduled.", {
                    requestId: response.context_mesh_refresh.request_id
                });
            }
            return response;
        } catch (error) {
            const message = error instanceof Error ? error.message : "Synchronization failed";
            refreshGenerationRef.current += 1;
            setLoading(false);
            mutateRows(p => ({
                ...p,
                rows: p.rows.map(row => ({
                    ...row,
                    links: row.links.map(existing => existing.id === (typeof link === "number" ? link : link.id)
                        ? { ...existing, syncStatus: "failed" as const, lastSyncError: message }
                        : existing)
                }))
            }));
            throw error;
        }
    }, [mutateRows]);

    return { providers, targets, loading, error, refresh, linkTarget, linkProduct, unlink, syncLink };
}
